import _, { get, isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Alert,
  Box,
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Snackbar,
  Typography,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import { Validate } from './validate';
import { TenantContext } from '../../orbit-context';
import { useTranslation } from 'react-i18next';
import { ADMIN_GROUP_ID } from '../../services/constants';

interface CheckboxGroupsInputProps<T> extends CheckboxProps {
  name: string;
  label?: React.ReactNode;
  validate?: Validate[];
  options: Array<T>;
  getOptionKey?: (option: T) => React.Key;
  getOptionLabel?: (option: T) => string | number;
  getOptionValue?: (option: T) => unknown;
  getDisabled?: (option: T) => boolean;
  row?: boolean;
  disabled?: boolean;
  defaultValues?: any;
  onCallBack?: (value: unknown) => void;
  style?: React.CSSProperties;
}

const defaultGetOptionKey = (option) => get(option, 'id');
const defaultGetOptionLabel = (option) => get(option, 'label');
const defaultGetOptionValue = (option) => get(option, 'value');

function getDefaultCheckboxValue(control: any, name: string) {
  const defaultValue = get(control.defaultValuesRef.current, name, []);
  if (!isEmpty(defaultValue)) {
    return defaultValue;
  }

  return name === 'acl.readable' ? [ADMIN_GROUP_ID] : [];
}

export default function CheckboxGroupsInput<T>(props: CheckboxGroupsInputProps<T>) {
  const {
    name, label = name,
    validate = [], row,
    options,
    disabled,
    getOptionKey = defaultGetOptionKey,
    getOptionLabel = defaultGetOptionLabel,
    getOptionValue = defaultGetOptionValue,
    getDisabled = () => disabled,
    ...rest
  } = props;
  const [t] = useTranslation();

  const methods = useFormContext();
  const { errors, control, getValues } = methods;
  const defaultValue = getDefaultCheckboxValue(control, name);
  const tenant = useContext(TenantContext);
  const error = get(errors, name);
  const errorMessage = get(errors, [name, 'message']);
  const [berror, setBError] = useState('');
  const validations = validate.reduce((acc, v) => Object.assign(acc, v), {});
  const required = Boolean(get(validations, 'required', false));

  const getOptionSelected = (values = [], option) => {
    return (values ?? []).includes(getOptionValue(option));
  };

  const styles: any = createStyles({
    formControlLabel: {
      fontSize: '0.9rem',
      '& label': { fontSize: '0.9rem' }
    },
    disableformControlLabel: {
      fontSize: '0.9rem',
      color: 'gray',
      '& label': { fontSize: '0.9rem' }
    }
  });

  const handleChange = (e, isChecked, onChange) => {
    const oldValues = getValues(name) ?? [];
    const value = e.target.value;

    if (value === 'industry' && isChecked) {
      if ((!tenant?.info?.industries) || tenant?.info?.industries?.length === 0) {
        onChange([].concat(oldValues.filter(v => v !== value)));
        if (berror.length === 0) {
          setBError(t('seetings.industryTips'));
        }
        return;
      }
    }

    if (value === 'businesstype' && isChecked) {
      const btList = _.get(tenant, ['info', 'businessType'], []);
      if (_.isEmpty(btList)) {
        onChange([].concat(oldValues.filter(v => v !== value)));
        if (berror.length === 0) {
          setBError(t('seetings.bussinessTips'));
        }
        return;
      }
    }

    const newValues = isChecked
      ? [value].concat(oldValues)
      : [].concat(oldValues.filter(v => v !== value));

    onChange(newValues);
  };

  return (
    <FormControl
      {...rest}
      component="fieldset"
      required={required}
      error={Boolean(error)}
      margin="normal"
    >
      {berror.length > 0 && (
        <Snackbar open={berror.length !== 0} autoHideDuration={3000} onClose={() => setBError('')}>
          <Alert onClose={() => setBError('')} severity="error">{berror}</Alert>
        </Snackbar>
      )}

      <FormGroup row={row} title={typeof label === 'string' ? label : undefined}>
        {options.map((option) => (
          <Controller
            key={getOptionKey(option)}
            control={control}
            name={name}
            rules={validations}
            defaultValue={defaultValue}
            render={({ onChange, onBlur, value, name }) => (
              <Box>
                <FormControlLabel
                  label={
                    typeof label === 'string' || label == null
                      ? (
                        <Typography style={getDisabled(option) ? styles.disableformControlLabel : styles.formControlLabel}>
                          {getOptionLabel(option)}
                        </Typography>
                      )
                      : (
                        <Typography style={getDisabled(option) ? styles.disableformControlLabel : styles.formControlLabel}>
                          {getOptionLabel(option)}
                        </Typography>
                      )
                  }
                  control={(
                    <Checkbox
                      color="primary"
                      name={name}
                      checked={getOptionSelected(value, option)}
                      value={getOptionValue(option)}
                      onChange={(e, isChecked) => {
                        handleChange(e, isChecked, onChange);
                      }}
                      onBlur={onBlur}
                      disabled={getDisabled(option)}
                      {...rest}
                    />
                  )}
                />
              </Box>
            )}
          />
        ))}
      </FormGroup>
      <Typography variant="caption" color="error">
        {errorMessage}
      </Typography>
    </FormControl>
  );
}

export function RadioboxGroupsInput<T>(props: CheckboxGroupsInputProps<T>) {
  const {
    name, label = name,
    validate = [], row,
    options,
    disabled,
    defaultValues,
    onCallBack,
    style,
    getOptionKey = defaultGetOptionKey,
    getOptionLabel = defaultGetOptionLabel,
    getOptionValue = defaultGetOptionValue,
    getDisabled = () => disabled,
    ...rest
  } = props;

  const methods = useFormContext();
  const { errors, control, getValues } = methods;
  const defaultValue = get(defaultValues ?? control.defaultValuesRef.current, name, '') ?? '';
  const [radioValue, setRadioValue] = useState(defaultValue);
  const getValueByName = getValues(name);

  useEffect(() => {
    if (!isEmpty(getValueByName)) {
      setRadioValue(getValueByName);
    }
  }, [getValueByName]);

  const error = get(errors, name);
  const errorMessage = get(errors, [name, 'message']);
  const [berror, setBError] = useState('');
  const validations = validate.reduce((acc, v) => Object.assign(acc, v), {});
  const required = Boolean(get(validations, 'required', false));

  const getOptionSelected = (value, option) => {
    return value === getOptionValue(option);
  };

  const styles: any = createStyles({
    formControlLabel: {
      fontSize: '0.9rem',
      '& label': { fontSize: '0.9rem' }
    }
  });

  const handleChange = (e, isChecked, onChange) => {
    if (!isChecked) {
      return;
    }

    const value = e.target.value;
    onChange(value);
    onCallBack?.(value);
  };

  return (
    <FormControl
      style={style ?? {}}
      component="fieldset"
      required={required}
      error={Boolean(error)}
      margin="normal"
    >
      {berror.length > 0 && (
        <Snackbar open={berror.length !== 0} autoHideDuration={3000} onClose={() => setBError('')}>
          <Alert onClose={() => setBError('')} severity="error">{berror}</Alert>
        </Snackbar>
      )}
      <FormGroup row={row} title={typeof label === 'string' ? label : undefined}>
        <Controller
          control={control}
          name={name}
          rules={validations}
          defaultValue={defaultValue}
          render={({ onChange, onBlur, value, name }) => (
            <RadioGroup row={row} value={radioValue}>
              {options.map((option) => (
                <FormControlLabel
                  key={getOptionKey(option)}
                  label={<Typography style={styles.formControlLabel}>{getOptionLabel(option)}</Typography>}
                  value={getOptionValue(option)}
                  onChange={(e, isChecked) => {
                    handleChange(e, isChecked, onChange);
                  }}
                  control={(
                    <Radio
                      color="primary"
                      name={name}
                      checked={getOptionSelected(value, option)}
                      onBlur={onBlur}
                      disabled={getDisabled(option)}
                      {...rest}
                    />
                  )}
                />
              ))}
            </RadioGroup>
          )}
        />
      </FormGroup>
      <Typography variant="caption" color="error">
        {errorMessage}
      </Typography>
    </FormControl>
  );
}

export function UploadCheckboxGroupsInput<T>(props: CheckboxGroupsInputProps<T>) {
  const {
    name, label = name,
    validate = [], row,
    options,
    disabled,
    getOptionKey = defaultGetOptionKey,
    getOptionLabel = defaultGetOptionLabel,
    getOptionValue = defaultGetOptionValue,
    getDisabled = () => disabled,
    ...rest
  } = props;

  const methods = useFormContext();
  const { errors, control, getValues } = methods;
  const defaultValue = getDefaultCheckboxValue(control, name);
  const error = get(errors, name);
  const errorMessage = get(errors, [name, 'message']);
  const validations = validate.reduce((acc, v) => Object.assign(acc, v), {});
  const required = Boolean(get(validations, 'required', false));

  const getOptionSelected = (values = [], option) => {
    return (values ?? []).includes(getOptionValue(option));
  };

  const styles: any = createStyles({
    formControlLabel: {
      fontSize: '0.9rem',
      '& label': { fontSize: '0.9rem' }
    }
  });

  const handleChange = (e, isChecked, onChange) => {
    const oldValues = getValues(name) ?? [];
    const value = e.target.value;
    const newValues = isChecked
      ? [value].concat(oldValues)
      : [].concat(oldValues.filter(v => v !== value));
    onChange(newValues);
  };

  return (
    <FormControl
      component="fieldset"
      required={required}
      error={Boolean(error)}
      margin="normal"
    >
      {label ? <Typography component="div">{label}</Typography> : null}
      <FormGroup row={row} title={typeof label === 'string' ? label : undefined}>
        {options.map((option) => (
          <Controller
            key={getOptionKey(option)}
            control={control}
            name={name}
            rules={validations}
            defaultValue={defaultValue}
            render={({ onChange, onBlur, value, name }) => (
              <FormControlLabel
                label={<Typography style={styles.formControlLabel}>{getOptionLabel(option)}</Typography>}
                control={(
                  <Checkbox
                    color="primary"
                    name={name}
                    checked={getOptionSelected(value, option)}
                    value={getOptionValue(option)}
                    onChange={(e, isChecked) => {
                      handleChange(e, isChecked, onChange);
                    }}
                    onBlur={onBlur}
                    disabled={getDisabled(option)}
                    {...rest}
                  />
                )}
              />
            )}
          />
        ))}
      </FormGroup>
      <Typography variant="caption" color="error">
        {errorMessage}
      </Typography>
    </FormControl>
  );
}
